// Транспортные правила API: заголовки, подпись изменяющих запросов, идемпотентность — tech/03 §1.
// Подпись считается одинаково на клиенте и сервере через WebCrypto (есть и в браузере, и в Node ≥ 20).
import { z } from 'zod';
import { Uuid7 } from './common.js';

export const API_PREFIX = '/api/v1';

export const Header = {
  authorization: 'authorization',
  clientBuild: 'x-client-build', // 03 §1 «Версия»
  serverTime: 'x-server-time', // 03 §1 «Время», мс
  reqTs: 'x-req-ts',
  reqNonce: 'x-req-nonce',
  reqSig: 'x-req-sig',
  idempotencyKey: 'idempotency-key',
  retryAfter: 'retry-after',
} as const;

/** Окно подписи ±60 с (03 §1). */
export const SIGNATURE_WINDOW_MS = 60_000;
/** Ключ идемпотентности не дальше 24 ч от часов сервера (03 §1). */
export const IDEMPOTENCY_KEY_MAX_SKEW_MS = 24 * 3600_000;
/** Кэш ответа по ключу в Redis — 10 мин (03 §1, Т-К-43). */
export const IDEMPOTENCY_CACHE_TTL_S = 600;

/** ULID: 26 символов Crockford base32 (03 §1 `X-Req-Nonce`). */
export const Ulid = z.string().regex(/^[0-9A-HJKMNP-TV-Z]{26}$/);

export const SignatureHeaders = z.object({
  [Header.reqTs]: z.coerce.number().int().positive(),
  [Header.reqNonce]: Ulid,
  [Header.reqSig]: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
});
export const IdempotencyHeader = z.object({ [Header.idempotencyKey]: Uuid7 });

/** Время из UUID v7 (первые 48 бит — мс). Сервер проверяет «не дальше 24 ч», а ledger_idem берёт из него день (02 §4). */
export function uuid7TimeMs(id: string): number {
  return parseInt(id.replace(/-/g, '').slice(0, 12), 16);
}

/**
 * Каноническая строка подписи (03 §1): `METHOD\nPATH\nTS\nNONCE\nSHA256(body)`.
 * Уточнения каркаса (Р-04): PATH — путь от корня домена вместе с `/api/v1` и строкой запроса, как отправлен;
 * SHA256(body) — hex в нижнем регистре от сырых байтов тела, пустое тело — хеш пустой строки.
 */
export function canonicalString(method: string, path: string, ts: number, nonce: string, bodySha256Hex: string): string {
  return `${method.toUpperCase()}\n${path}\n${ts}\n${nonce}\n${bodySha256Hex}`;
}

const enc = new TextEncoder();

export function toBase64Url(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function fromBase64Url(s: string): Uint8Array {
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (s.length % 4)) % 4);
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function toHex(buf: ArrayBuffer): string {
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

export async function sha256Hex(body: string | Uint8Array): Promise<string> {
  const data = typeof body === 'string' ? enc.encode(body) : body;
  return toHex(await crypto.subtle.digest('SHA-256', data as BufferSource));
}

/**
 * `X-Req-Sig` = base64url(HMAC-SHA256(session_key, canonical)). `sessionKey` приходит из /auth/telegram
 * как base64url 32 байт (Р-04); ключом HMAC служат раскодированные байты.
 */
export async function signRequest(p: {
  sessionKey: string;
  method: string;
  path: string;
  ts: number;
  nonce: string;
  body: string;
}): Promise<string> {
  const key = await crypto.subtle.importKey(
    'raw', fromBase64Url(p.sessionKey) as BufferSource, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign'],
  );
  const msg = canonicalString(p.method, p.path, p.ts, p.nonce, await sha256Hex(p.body));
  return toBase64Url(new Uint8Array(await crypto.subtle.sign('HMAC', key, enc.encode(msg))));
}
